import { useMemo } from "react";
import useGetPrevisaoEstoque from "../hooks/useGetPrevisaoEstoque";
import {
  calcAreaTotalOs,
  calcPrevisaoEstoque,
  calcProgramadoNestaOsFromRecurso,
} from "./calc";
import { formatInsumoLabel } from "./format";
import type {
  LocalEstoqueInput,
  PrevisaoEstoqueResult,
  RecursoEstoqueInput,
} from "./types";

interface UsePrevisaoEstoqueOsParams {
  recursos: RecursoEstoqueInput[];
  locais: LocalEstoqueInput[];
  fazendaId?: string;
  ordemServicoId?: string;
}

const EMPTY: PrevisaoEstoqueResult = {
  linhas: [],
  alertaByRecursoId: {},
  countNegativos: 0,
  isLoading: false,
  isError: false,
};

export function usePrevisaoEstoqueOs({
  recursos,
  locais,
  fazendaId,
  ordemServicoId,
}: UsePrevisaoEstoqueOsParams): PrevisaoEstoqueResult {
  const alocados = useMemo(
    () => recursos.filter(r => r.insumoId),
    [recursos],
  );

  const insumoIds = useMemo(
    () => Array.from(new Set(alocados.map(r => r.insumoId!).filter(Boolean))),
    [alocados],
  );

  const {
    data: apiItems = [],
    isLoading,
    isFetching,
    isError,
  } = useGetPrevisaoEstoque(fazendaId, insumoIds, ordemServicoId);

  return useMemo(() => {
    if (insumoIds.length === 0) {
      return { ...EMPTY, isLoading: false, isError: false };
    }

    const areaTotalOs = calcAreaTotalOs(locais);

    const programadoPorInsumo = new Map<string, number>();
    alocados.forEach(recurso => {
      const id = recurso.insumoId!;
      const atual = programadoPorInsumo.get(id) ?? 0;
      programadoPorInsumo.set(
        id,
        atual + calcProgramadoNestaOsFromRecurso(recurso, areaTotalOs),
      );
    });

    const apiByInsumoId = new Map(apiItems.map(item => [item.insumoId, item]));

    const metaPorInsumo = new Map<string, RecursoEstoqueInput>();
    alocados.forEach(r => {
      if (!metaPorInsumo.has(r.insumoId!)) {
        metaPorInsumo.set(r.insumoId!, r);
      }
    });

    const linhas = Array.from(metaPorInsumo.entries()).map(([insumoId, meta]) => {
      const api = apiByInsumoId.get(insumoId);
      const estoqueFazenda = api?.estoqueFazenda ?? 0;
      const fatorEmbalagem = api?.fatorEmbalagem ?? 0;
      const programadoEmOs = api?.programadoEmOs ?? 0;
      const programadoNestaOs = programadoPorInsumo.get(insumoId) ?? 0;
      const previsaoEstoque = calcPrevisaoEstoque(
        estoqueFazenda,
        programadoEmOs,
        programadoNestaOs,
      );

      return {
        insumoId,
        insumoLabel: formatInsumoLabel(
          meta.insumoLabel,
          meta.insumoUnidadeMedidaDescricao,
          meta.unidade as string | undefined,
        ),
        estoqueFazenda,
        fatorEmbalagem,
        programadoEmOs,
        programadoNestaOs,
        previsaoEstoque,
        temAlerta: previsaoEstoque < 0,
      };
    });

    const alertaByInsumoId = new Map(
      linhas.filter(l => l.temAlerta).map(l => [l.insumoId, true]),
    );

    const alertaByRecursoId: Record<number, boolean> = {};
    alocados.forEach(recurso => {
      if (recurso.insumoId && alertaByInsumoId.has(recurso.insumoId)) {
        alertaByRecursoId[recurso.id] = true;
      }
    });

    return {
      linhas,
      alertaByRecursoId,
      countNegativos: linhas.filter(l => l.temAlerta).length,
      isLoading: isLoading || isFetching,
      isError,
    };
  }, [alocados, locais, insumoIds, apiItems, isLoading, isFetching, isError]);
}
